<template>
  <modal :id="modalId" :title="title">
    <div v-if="error">
      <div class="alert alert-danger text-center">
        {{ error }}
      </div>
    </div>

    <!-- Task -->
    <div class="form-group row">
      <label for="task_type" class="col-sm-2 col-form-label">Task</label>
      <div class="col-sm-10">
        <select id="task_type" class="form-control" v-model="fields.task_type_id">
          <option v-for="(taskType, typeId) in types" :value="typeId">{{ taskType }}</option>
        </select>
        <div v-if="(fields.task_type_id > 0) && (types[fields.task_type_id] == null)" class="text-warning">An invalid or inactive task has been loaded. Please select a new task.</div>
        <div v-if="errors && errors.task_type_id" class="text-danger">{{ errors.task_type_id[0] }}</div>
      </div>
    </div>

    <!-- Custom task text -->
    <div v-if="showCustomTextField" class="form-group row">
      <label for="text" class="col-sm-2 col-form-label">Task Text</label>
      <div class="col-sm-10">
        <textarea class="form-control" v-model="fields.text" rows="5"></textarea>
        <div v-if="errors && errors.text" class="text-danger">{{ errors.text[0] }}</div>
      </div>
    </div>

    <!-- Due date -->
    <div class="form-group row">
      <label class="col-sm-2 col-form-label">Due Date:</label>
      <div class="col-sm-10">
        <div v-if="expiredDueDate">
          <strong class="text-danger">{{ expiredDueDate }}</strong>
          <small class="text-warning">- the saved due date is expired.</small>
        </div>
        <div>
          <div class="mb-1">
            <datepicker
              v-model="fields.due_date"
              ref="datepicker"
              :min-date="minDate"
              :disabled="!enableDatePicker"
              @closed="selectDate()"
              placeholder="Select a date">
            </datepicker>
          </div>
          <div class="btn-group btn-group-sm d-flex" data-toggle="buttons">
            <button v-for="days in dayOptions" class="btn btn-info btn-sm" @click="addDays(days)">
              <input type="radio" name="days"> {{ days }} days
            </button>
            <button class="btn btn-info btn-sm" @click="activateDatePicker()">
              <input type="radio" name="days"> Other
            </button>
          </div>
        </div>
        <div v-if="errors && errors.due_date" class="text-danger">{{ errors.due_date[0] }}</div>
      </div>
    </div>

    <!-- Assignees -->
    <div class="form-group row">
      <label class="col-sm-2 col-form-label">Assignees:</label>
      <div class="col-sm-10 pt-2">
        <table class="table">
          <tr v-for="(userTypeLabel, userType) in userTypes">
            <td class="td-small">
              <a :class="'btn btn-xs btn-outline-secondary btn-iconbox' + (usersByType[userType].id !== 0 ? '' : ' disabled')"
                 @click="toggleUserTypeSelection(userType)">
                <i :class="'fa' + (userTypeSelected.indexOf(userType) === -1 ? '' : ' fa-check')"></i>
              </a>
              <span :class="(usersByType[userType].id !== 0 ? 'pointered' : 'text-muted')"
                    @click="toggleUserTypeSelection(userType)">{{ userTypeLabel }}</span>
            </td>
            <td>
              <a v-if="usersByType[userType].id !== 0" :href="usersByType[userType].link" target="_blank">{{ usersByType[userType].name }}</a>
              <span v-if="usersByType[userType].replaced" class="text-danger">
                <i class="fa fa-exclamation-triangle"></i> {{ userTypeLabel }} was changed to
                <strong>
                  <a :href="usersByType[userType].replaced.link" class="text-danger"
                     target="_blank">{{ usersByType[userType].replaced.name }}</a>.
                </strong>
              </span>
            </td>
          </tr>

          <tr v-for="(user, index) in customUsers">
            <td class="td-small">
              <a class="btn btn-xs btn-outline-secondary btn-iconbox" @click="removeUser(user)"><i class="fa fa-close"></i></a>
              <span class="pointered" @click="removeUser(user)">Selected user #{{index+1}}</span>
            </td>
            <td>
              <a :href="user.link" target="_blank">{{ user.name }}</a>
            </td>
          </tr>

          <tr v-if="errors && errors.assignees">
            <td colspan="2">
              <div class="text-danger">{{ errors.assignees[0] }}</div>
            </td>
          </tr>

          <tr>
            <td colspan="2">
              <user-typeahead v-model="newUser" @userSelected="addNewUser()" placeholder="Find a user by name or email address and add to assignees..." class="pt-0 mt-0"></user-typeahead>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <!-- Form actions -->
    <template #footer>
      <button type="button" class="btn btn-default" @click="hideModal">Close</button>
      <button type="submit" class="btn btn-primary" @click="submit" :disabled="formIsProcessing"><i
        class="fa fa-fw fa-save"></i>Save
      </button>
    </template>

  </modal>
</template>

<script>
import userTypeahead from "../UserTypeahead.vue"
import HasModal from "./../mixins/HasModal";

const DATE_FORMAT = 'MM/DD/YYYY';

export default {
  mixins: [HasModal],

  components: {
    userTypeahead
  },

  /**
   * Input properties
   */
  props: {

    // Claim id.
    claimId: {
      type: Number,
      default: 0
    },

    // Task id.
    taskId: {
      type: Number,
      default: 0
    },

    // Initial task.
    initialTask: {
      type: Object,
      default: () => {
        return {
          text: '',
          due_date: ''
        }
      }
    },

    // Modal dialog id.
    modalId: {
      type: String,
      default: "taskFormModal"
    },

    // Modal dialog title.
    title: {
      type: String,
      default: "Add task"
    },

    // Task list component reference.
    taskListRef: {
      type: String,
      default: 'task_list'
    },

    // Task type list keyed by id.
    taskTypes: {
      type: Object,
      default: () => {}
    },

    // User types.
    userTypes: {
      type: Object,
      default: () => {}
    },

    // Claim users by type.
    claimUsers: {
      type: Object,
      default: () => {}
    },
  },

  /**
   * Variables (models)
   */
  data() {
    return {
      // Current task object.
      task: {},

      // Form fields.
      fields: {},

      // Selected user type options.
      userTypeSelected: [],

      // Custom users (find and manually added).
      customUsers: [],

      // New user.
      newUser: {},

      // Due date selector options.
      dayOptions: [7, 14, 21, 30, 45, 60],

      // Due date datepicker widget state.
      enableDatePicker: false,

      // Expired due date.
      expiredDueDate: '',

      // Form validation error messages.
      errors: {},

      // Internal validation state.
      isValid: true,

      // Common error message.
      error: '',

      // Form is processing now indicator.
      formIsProcessing: false,
    }
  },

  /**
   * Computed values
   */
  computed: {
    // Is form in "edit" mode.
    isEdit() {
      return this.taskId !== 0;
    },

    // Task types and "other" option.
    types() {
      let types = this.taskTypes;
      types[''] = 'Other';
      return types;
    },

    // Selected task type.
    type() {
      return this.fields.task_type_id;
    },

    // Show custom text field if "Other" task type selected.
    showCustomTextField() {
      return this.type === '';
    },

    // Users by type.
    usersByType() {
      if (!this.isEdit || (this.task.assignee_list === undefined)) return this.claimUsers;

      let items = _.cloneDeep(this.claimUsers);
      this.task.assignee_list.forEach(assignee => {
        let type = assignee.type;
        if (type === '') return;

        let replaced = null;
        if (assignee.id !== items[type].id) {
          replaced = items[type];
        }
        items[type] = {
          id: assignee.id,
          name: assignee.name,
          link: assignee.link,
          replaced: replaced,
        };
      });
      return items;
    },

    minDate() {
      return moment(new Date()).set({hour:0,minute:0,second:0,millisecond:0}).format(DATE_FORMAT);
    }

  },

  /**
   * Functions (methods)
   */
  methods: {
    /**
     * Init form.
     */
    init() {
      this.clearForm();

      // Init task.
      this.task = _.cloneDeep(this.initialTask);
      this.fields = this.task;
      this.expiredDueDate = this.fields.expired ? this.fields.due_date : '';

      // Load selected users.
      this.loadSelectedUsers();
    },

    loadSelectedUsers() {
      if (!this.isEdit) return;

      this.task.assignee_list.forEach(a => {
        if (a.type !== '') {
          this.userTypeSelected.push(a.type);
        } else {
          this.customUsers.push(a);
        }
      });
    },

    setDate(date) {
      if (date) {
        this.date = moment(date, DATE_FORMAT).format(DATE_FORMAT);
        this.fields.due_date = date;
        this.enableDatePicker = false;
      }
    },
    selectDate() {
      this.setDate(this.fields.due_date);
    },
    addDays(days) {
      var calculatedDate = moment(new Date()).add(days, 'days').format(DATE_FORMAT);
      this.setDate(calculatedDate);
      this.enableDatePicker = false;
    },
    activateDatePicker() {
      this.enableDatePicker = true;
      this.$nextTick(function () {
        this.$refs.datepicker.openMenu();
      });
    },

    /**
     * Toggle user type in the list of selected types.
     */
    toggleUserTypeSelection(userType) {
      if (this.usersByType[userType].id === 0) return;

      const index = this.userTypeSelected.indexOf(userType);
      if (index === -1) {
        this.userTypeSelected.push(userType);
      } else {
        this.userTypeSelected.splice(index, 1);
      }
    },

    /**
     * Add user to assignees.
     */
    addNewUser() {
      if (this.newUser) {
        let userIndex = this.customUsers.findIndex(a => a.id === this.newUser.id);
        if (userIndex === -1) {
          this.customUsers.push({
            id: this.newUser.id,
            name: (this.newUser.first_name + ' ' + this.newUser.last_name).trim(),
            link: 'users/' + this.newUser.id,
          });
        }
        this.newUser = {};
      }
    },

    /**
     * Remove user from assignees.
     *
     * @param user
     */
    removeUser(user) {
      const index = this.customUsers.findIndex(a => a.id === user.id);
      if (index !== -1) {
        this.customUsers.splice(index, 1);
      }
    },

    /**
     * Submit form data.
     */
    submit() {
      this.validate();
      if (!this.isValid) return;

      if (!this.isEdit) {
        this.storeTask();
      } else {
        if (this.contactId < 0) {
          this.storeTask();
        } else {
          this.updateTask();
        }
      }
    },

    /**
     * Create task.
     */
    storeTask() {
      let formData = this.formData();
      this.formIsProcessing = true;
      axios.post('/claims/' + this.claimId + '/tasks', formData)
        .then(response => {
          this.hideModal();
          this.$root.$refs[this.taskListRef].loadTasks();
          this.init();
        })
        .catch(error => {
          this.handleErrors(error, 'Unable to create task')
        })
        .finally(() => {
          this.formIsProcessing = false;
        });
    },

    /**
     * Save task.
     */
    updateTask() {
      let formData = this.formData();
      formData.append('_method', "PATCH");
      this.formIsProcessing = true;
      axios.post('/claims/' + this.claimId + '/tasks/' + this.taskId, formData)
        .then(response => {
          this.hideModal();
          this.$root.$refs[this.taskListRef].loadTasks();
        })
        .catch(error => {
          this.handleErrors(error, 'Unable to save data')
        })
        .finally(() => {
          this.formIsProcessing = false;
        });
    },

    /**
     * Prepare form data.
     */
    formData() {
      const formData = new FormData();
      Object.keys(this.fields).forEach(fieldName => {
        const value = this.fields[fieldName] ? this.fields[fieldName] : '';
        formData.append(fieldName, value);
      });

      if (!formData.get('task_type_id')) {
        formData.delete('task_type_id');
      } else {
        formData.set('text', "");
      }

      let assignees = [];
      this.userTypeSelected.forEach(userType => assignees.push({user_id: this.claimUsers[userType].id, type: userType}));
      this.customUsers.forEach(user => assignees.push({user_id: user.id}));
      formData.append('assignees', JSON.stringify(assignees));

      /*console.group('FORM DATA');
      for (var pair of formData.entries()) {
          console.log(pair[0]+ ', ' + pair[1]);
      }
      console.groupEnd();*/

      return formData;
    },

    /**
     * Validate form.
     */
    validate() {
      this.errors = {};
      this.isValid = true;
      this.validateForm();
    },

    /**
     * Validate task form.
     */
    validateForm() {
      if (this.fields.task_type_id === undefined) {
        this.setError('task_type_id', 'Please select a task.');
      }
      if ((this.fields.task_type_id === '')) {
        if (this.fields.text.trim() === '') {
          this.setError('text', 'Please specify a task text.');
        }
      }
      if (!this.fields.due_date) {
        this.setError('due_date', 'Please specify a due date.');
      }
      if ((this.userTypeSelected.length + this.customUsers.length) === 0) {
        this.setError('assignees', 'Please select one or more assignees.');
      }
    },

    /**
     * Set validation error.
     */
    setError(field, message) {
      if (!this.errors[field]) this.errors[field] = [];
      this.errors[field].push(message);
      this.isValid = false;
    },

    /**
     * Handle axios errors.
     *
     * @param errorObj
     * @param defaultMessage
     */
    handleErrors(errorObj, defaultMessage) {
      if (errorObj.response.status === 422) {
        this.errors = errorObj.response.data.errors || {};
        Object.keys(this.errors).forEach(fieldName => {
          if (fieldName.startsWith('assignees.') && fieldName.endsWith('.user_id')) {
            this.errors.assignees = this.errors.assignees || [];
            this.errors.assignees.push('An error occurred while saving assignees. One or more users were not found.');
          }
          if (fieldName.startsWith('assignees.') && fieldName.endsWith('.type')) {
            this.errors.assignees = this.errors.assignees || [];
            this.errors.assignees.push('An error occurred while saving assignees. Invalid assignee type specified.');
          }
        });
      } else {
        if (errorObj.response.status === 403) {
          this.error = defaultMessage + ': access denied.';
        } else {
          this.error = defaultMessage;
        }
      }
    },

    /**
     * Clear form fields and messages.
     */
    clearForm() {
      this.fields = {};
      this.userTypeSelected = [];
      this.customUsers = [];
      this.errors = {};
      this.error = '';
    }
  },

  /**
   * Initialize component on mount.
   */
  mounted() {
    this.init();
  },

}
</script>

<style scoped>
.btn:focus, .btn.focus {
  outline: none;
}

[data-toggle=buttons] > .btn input[type=radio], [data-toggle=buttons] > .btn-static input[type=radio], [data-toggle=buttons] > .btn input[type=checkbox], [data-toggle=buttons] > .btn-static input[type=checkbox], [data-toggle=buttons] > .btn-group > .btn input[type=radio], [data-toggle=buttons] > .btn-group > .btn input[type=checkbox] {
  position: absolute;
  clip: rect(0, 0, 0, 0);
  pointer-events: none;
}

.col-form-label {
  padding: 7px 0 0 0;
}

.td-small {
  width: 200px;
}

.btn-iconbox {
  padding: 1px;
  width: 20px;
  height: 20px;
  margin-right: 5px;
}

.pointered {
  cursor: pointer;
}

.readonly-field {
  padding-top: 7px;
}
</style>
